import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/redux/root';
import {
  attachDepositBonusToUser,
  fetchDepositBonuses,
  fetchUserDepositBonuses,
} from '@/services/deposit-bonus/deposit-bonus-thunks';
import { DepositBonusStatus } from '@repo/common-types';
import { Input, Form, FormGroup, Label, Button } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useGetDepositBonusesQuery, useGetUserDepositBonusesQuery } from '@/redux/api/depositBonus';

const AttachUserDepositBonus: React.FC = () => {
  const { userId } = useParams();

  const dispatch = useDispatch<AppDispatch>();

  const { data: depositBonusList } = useGetDepositBonusesQuery();

  const { data: userDepositBonuses } = useGetUserDepositBonusesQuery(userId as string);

  const userActiveBonus = userDepositBonuses?.find((bonus) => bonus.status === DepositBonusStatus.ACTIVE);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [depositBonusId, setDepositBonusId] = useState<string | null>(null);
  const [depositAmount, setDepositAmount] = useState<number | null>(null);

  const formValid = useMemo(() => {
    return depositBonusId && depositAmount && startDate;
  }, [depositBonusId, depositAmount, startDate]);

  const onSubmit = () => {
    if (!userId || !depositBonusId || !depositAmount || !startDate) {
      return;
    }

    dispatch(
      attachDepositBonusToUser({
        depositBonusId,
        depositAmount,
        startDate,
        userId,
      }) as any,
    );

    toast.success('Bonus applied successfully');
    setDepositBonusId(null);
    setDepositAmount(null);
    setStartDate(null);
  };

  useEffect(() => {
    if (depositBonusId === userActiveBonus?.depositBonusId) {
      setDepositAmount(userActiveBonus.currentDeposit);
      setStartDate(userActiveBonus?.depositedAt ? format(userActiveBonus?.depositedAt, 'yyyy-MM-dd') : null);
    }
  }, [depositBonusId, userActiveBonus]);

  useEffect(() => {
    dispatch(fetchDepositBonuses() as any);
    if (userId) {
      dispatch(fetchUserDepositBonuses(userId) as any);
    }
  }, []);

  return (
    <div>
      <h3>Attach Deposit Bonus:</h3>
      {userActiveBonus && (
        <div className="w-100 d-flex justify-content-between my-4 flex-row">
          <div className="lex-col flex">
            <h5>Bonus Name</h5>
            <p>{userActiveBonus.depositBonus?.name}</p>
          </div>
          <div className="flex flex-col">
            <h5>Deposit Amount</h5>
            <p>{userActiveBonus.currentDeposit?.toFixed(2)}</p>
          </div>
          <div className="flex flex-col">
            <h5>Earned</h5>
            <p>{userActiveBonus.bonusEarned?.toFixed(2)}</p>
          </div>
          <div className="flex flex-col">
            <h5>Rake Target</h5>
            <p>{userActiveBonus.rakeTarget?.toFixed(2)}</p>
          </div>
          <div className="flex flex-col">
            <h5>Current Rake Progress</h5>
            <p>{userActiveBonus.currentRakeProgress?.toFixed(2)}</p>
          </div>
          <div className="flex flex-col">
            <h5>Deposited At (GMT)</h5>
            <p>{userActiveBonus.depositedAt ? new Date(userActiveBonus.depositedAt).toUTCString() : 'N/A'}</p>
          </div>
        </div>
      )}
      <h6>Apply new bonus:</h6>
      <Form onSubmit={onSubmit}>
        <FormGroup>
          <Label for="depositBonus">DepositBonus</Label>
          <select
            className="form-select"
            value={depositBonusId ?? ''}
            onChange={(e) => setDepositBonusId(e.target.value)}
          >
            <option value={undefined}>-</option>
            {(depositBonusList ?? [])?.map((bonus) => (
              <option key={bonus.id} value={bonus.id}>
                {bonus.name}
              </option>
            ))}
          </select>
        </FormGroup>
        <FormGroup>
          <Label for="depositAmount">Deposit Amount</Label>
          <Input
            type="number"
            className="form-control"
            value={depositAmount ?? ''}
            onChange={(e) => setDepositAmount(Number(e.target.value))}
          />
        </FormGroup>
        <FormGroup>
          <Label for="startDate">Deposit Date (GMT)</Label>
          <Input
            type="date"
            className="form-control"
            value={startDate ?? ''}
            max={format(new Date(), 'yyyy-MM-dd')}
            onChange={(e) => {
              setStartDate(e.target.value);
            }}
          />
        </FormGroup>
        <Button disabled={!formValid} onClick={onSubmit} color="primary">
          Apply Bonus
        </Button>
      </Form>
    </div>
  );
};

export default AttachUserDepositBonus;
